import { Vector3, TransformNode } from '@babylonjs/core';
import { DEFAULT_MIN_NODE_PIXEL_SIZE } from './constants';
import { BoundingBox, Sphere } from './loading-v2/OctreeGeometry';
import { PointCloudMaterial } from './material/point-cloud-material';
import { PointCloudOctreeNode } from './point-cloud-octree-node';
import { PointCloudTree } from './point-cloud-tree';
import { SimplePointsCloudSystem } from './simple-point-cloud-system';
import { computeTransformedBoundingBox } from './utils/bounds';
const DEFAULT_ROTATION = new Vector3(-Math.PI / 2, 0, 0);
const DEFAULT_SCALE = new Vector3(-1, 1, 1);
export class PointCloudOctree extends PointCloudTree {
    potree;
    disposed = false;
    pcoGeometry;
    boundingBox;
    boundingSphere;
    level = 0;
    maxLevel = Infinity;
    /**
     * The minimum radius of a node's bounding sphere on the screen in order to be displayed.
     */
    minNodePixelSize = DEFAULT_MIN_NODE_PIXEL_SIZE;
    root = null;
    boundingBoxNodes = [];
    visibleNodes = [];
    visibleGeometry = [];
    numVisiblePoints = 0;
    showBoundingBox = false;
    visibleBounds = new BoundingBox(new Vector3(), new Vector3());
    // private picker: PointCloudOctreePicker | undefined;
    constructor(name, potree, pcoGeometry) {
        super();
        this.name = name;
        this.potree = potree;
        this.root = pcoGeometry.root;
        this.pcoGeometry = pcoGeometry;
        this.boundingBox = pcoGeometry.boundingBox;
        this.boundingSphere = this.boundingBox.getBoundingSphere(new Sphere());
        this.position.copyFrom(pcoGeometry.offset);
        this.rotation.copyFrom(DEFAULT_ROTATION);
        this.scaling.copyFrom(DEFAULT_SCALE);
        this.computeWorldMatrix(true);
        this.material = new PointCloudMaterial(this.name, this.getScene());
        // this.initMaterial(this.material);
    }
    // private initMaterial(material: PointCloudMaterial): void
    // {
    // 	this.computeWorldMatrix(true);
    // 	const {min, max} = computeTransformedBoundingBox(
    // 		this.pcoGeometry.tightBoundingBox || this.getBoundingBoxWorld(),
    // 		this.getWorldMatrix(),
    // 	);
    // 	const bWidth = max.z - min.z;
    // 	material.heightMin = min.z - 0.2 * bWidth;
    // 	material.heightMax = max.z + 0.2 * bWidth;
    // }
    dispose() {
        if (this.root) {
            this.root.dispose();
        }
        this.pcoGeometry.root.traverse((n) => {
            return this.potree.lru.remove(n);
        });
        this.pcoGeometry.dispose();
        this.material?.dispose();
        this.visibleNodes = [];
        this.visibleGeometry = [];
        // if (this.picker)
        // {
        // 	this.picker.dispose();
        // 	this.picker = undefined;
        // }
        this.disposed = true;
    }
    get pointSizeType() {
        return this.pointSizeType;
    }
    set pointSizeType(value) {
        this.pointSizeType = value;
    }
    toTreeNode(geometryNode, parent) {
        const points = new SimplePointsCloudSystem(geometryNode.name, 2, this.getScene(), this.material);
        const node = new PointCloudOctreeNode(geometryNode, points);
        points.name = geometryNode.name;
        points.position = geometryNode.boundingBox.min.clone();
        points.buildMeshFromGeometry(geometryNode.geometry);
        // points.mesh?.onBeforeRenderObservable.clear();
        points.mesh?.computeWorldMatrix(true);
        points.mesh?.onBeforeRenderObservable.add(PointCloudMaterial.makeOnBeforeRender(this, node));
        // points.mesh?.material?.onBindObservable.add(PointCloudMaterial.makeOnBeforeRender(this, node));
        // points.mesh?.material?.getEffect()?.onBindObservable.add(PointCloudMaterial.makeOnBeforeRender(this, node, (this.material as PointCloudMaterial)?.visibleNodeTextureOffsets));
        if (parent) {
            if (points.mesh && parent.sceneNode.mesh) {
                points.mesh.parent = parent.sceneNode.mesh;
            }
            parent.children[geometryNode.index] = node;
            points.position = geometryNode.boundingBox.min.subtract(parent.boundingBox.min);
            geometryNode.oneTimeDisposeHandlers.push(() => {
                node.disposeSceneNode();
                if (node.sceneNode.mesh) {
                    node.sceneNode.mesh.parent = null;
                }
                // Replace the tree node (rendered and in the GPU) with the geometry node.
                parent.children[geometryNode.index] = geometryNode;
            });
        }
        else {
            this.root = node;
            if (points.mesh) {
                points.mesh.parent = this;
            }
        }
        return node;
    }
    updateMaterial(camera, engine) {
        this.material.update(this, this.visibleNodes, camera, engine);
    }
    updateVisibleBounds() {
        const bounds = this.visibleBounds;
        bounds.min.set(Infinity, Infinity, Infinity);
        bounds.max.set(-Infinity, -Infinity, -Infinity);
        for (const node of this.visibleNodes) {
            if (node.isLeafNode) {
                bounds.min.minimizeInPlace(node.boundingBox.min);
                bounds.max.maximizeInPlace(node.boundingBox.max);
            }
        }
    }
    updateBoundingBoxes() {
        if (!this.showBoundingBox) {
            return;
        }
        let bbRoot = null;
        const nodes = this.parent
            ? this.parent.getChildren()
            : this.getScene()?.rootNodes;
        for (const child of nodes) {
            if (child.name === 'bbroot' && child instanceof TransformNode) {
                bbRoot = child;
                break;
            }
        }
        if (!bbRoot) {
            bbRoot = new TransformNode('bbroot', this.getScene());
            bbRoot.parent = this.parent;
        }
        const visibleBoxes = [];
        for (const node of this.visibleNodes) {
            if (node.boundingBoxNode !== undefined && node.isLeafNode) {
                visibleBoxes.push(node.boundingBoxNode);
            }
        }
        if (bbRoot) {
            // Add visible bounding boxes to the root node
            for (const box of visibleBoxes) {
                if (box) {
                    box.parent = bbRoot;
                }
            }
        }
    }
    computeWorldMatrix(force) {
        return super.computeWorldMatrix(force);
    }
    hideDescendants(object) {
        const toHide = [];
        addVisibleChildren(object);
        while (toHide.length > 0) {
            const objToHide = toHide.shift();
            objToHide.setEnabled(false);
            addVisibleChildren(objToHide);
        }
        function addVisibleChildren(obj) {
            const nodes = obj.getChildTransformNodes();
            for (const child of nodes) {
                if (child.isEnabled()) {
                    toHide.push(child);
                }
            }
        }
    }
    moveToOrigin() {
        this.position.set(0, 0, 0); // Reset, then the matrix will be updated in getBoundingBoxWorld()
        const center = this.getBoundingBoxWorld().centerWorld;
        this.position.set(-center.x, -center.y, -center.z);
    }
    moveToGroundPlane() {
        this.position.y += -this.getBoundingBoxWorld().min.y;
    }
    getBoundingBoxWorld() {
        this.computeWorldMatrix(true);
        return computeTransformedBoundingBox(this.boundingBox, this.getWorldMatrix());
    }
    getVisibleExtent() {
        return computeTransformedBoundingBox(this.visibleBounds, this.getWorldMatrix());
    }
    // pick(
    // 	renderer: Engine,
    // 	camera: Camera,
    // 	ray: Ray,
    // 	params: Partial<PickParams> = {},
    // ): PickPoint | null
    // {
    // 	this.picker = this.picker || new PointCloudOctreePicker();
    // 	return this.picker.pick(renderer, camera, ray, [this], params);
    // }
    get progress() {
        return this.visibleGeometry.length === 0
            ? 0
            : this.visibleNodes.length / this.visibleGeometry.length;
    }
}
